/**
 * Admin trials page for /admin/trials route.
 * Lists trial workspaces and lets an admin hard-reset a single trial (so its
 * device can trial again).
 */

import { useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import { useTrials, useResetTrial } from "../hooks/useAdminTrials";
import type { AdminTrial } from "../services/naraDoor";
import { Timer, RotateCcw } from "lucide-react";
import { KiiList, type KiiListColumn } from "../components/kii";
import { KiiConfirmDialog } from "../components/kii/kii-confirm-dialog";
import { KiiBadge } from "../components/kii/kii-badge";
import { TableRow, TableCell } from "../components/ui/table";
import { Button } from "@/components/ui/button";
import { publicRoutes } from "../lib/routes";

const columns: KiiListColumn[] = [
  { key: "device", label: "Device" },
  { key: "workspace", label: "Workspace" },
  { key: "created", label: "Created" },
  { key: "expires", label: "Expires" },
  { key: "recordings", label: "Recordings" },
  { key: "boosts", label: "Boosts" },
  { key: "status", label: "Status" },
  { key: "actions", label: "Actions" },
];

export function AdminTrials() {
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();

  const [resetTarget, setResetTarget] = useState<AdminTrial | null>(null);

  const { data: trialsData, isLoading: isTrialsLoading, error: trialsError } = useTrials();
  const resetTrial = useResetTrial();

  const handleConfirmReset = useCallback(() => {
    if (!resetTarget) return;
    resetTrial.mutate(resetTarget.workspace_id, {
      onSuccess: () => setResetTarget(null),
    });
  }, [resetTarget, resetTrial]);

  // Redirect non-admins to dashboard
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  const trials = trialsData?.trials ?? [];

  return (
    <div>
      <PageHeader
        title={t("adminTrials.title")}
        description={t("adminTrials.description")}
        isAdmin
        breadcrumbs={[
          { label: t("adminTrials.breadcrumb.dashboard"), href: publicRoutes.home },
          { label: t("adminTrials.breadcrumb.admin") },
          { label: t("adminTrials.breadcrumb.trials") },
        ]}
      />

      <KiiList<AdminTrial>
        columns={columns}
        data={trials}
        isLoading={isTrialsLoading}
        emptyMessage={trialsError ? t("adminTrials.error.loadTrials") : t("adminTrials.empty.noTrials")}
        emptyIcon={<Timer className="h-10 w-10 text-muted-foreground" />}
        showCount={true}
        countLabel={(count) => `${count} trial${count !== 1 ? "s" : ""}`}
        renderRow={(trial) => (
          <TableRow key={trial.workspace_id}>
            <TableCell className="font-mono text-xs">{trial.device_key}</TableCell>
            <TableCell>
              <div className="text-sm">
                {trial.workspace_name || t("adminTrials.unknown")}
                {trial.handle && (
                  <div className="text-xs text-muted-foreground">@{trial.handle}</div>
                )}
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">{formatDate(trial.created_at, "datetime")}</TableCell>
            <TableCell className="text-muted-foreground">{formatDate(trial.expires_at, "datetime")}</TableCell>
            <TableCell className="text-muted-foreground">{trial.recording_count}</TableCell>
            <TableCell className="text-muted-foreground">{trial.boost_balance ?? "—"}</TableCell>
            <TableCell>
              {trial.reaped_at ? (
                <KiiBadge variant="secondary">{t("adminTrials.status.reaped")}</KiiBadge>
              ) : (
                <KiiBadge variant="success">{t("adminTrials.status.active")}</KiiBadge>
              )}
            </TableCell>
            <TableCell>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setResetTarget(trial)}
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                {t("adminTrials.reset.button")}
              </Button>
            </TableCell>
          </TableRow>
        )}
      />

      {resetTrial.isError && (
        <p className="mt-2 text-sm text-destructive">{t("adminTrials.reset.error")}</p>
      )}

      <KiiConfirmDialog
        open={!!resetTarget}
        onOpenChange={(open) => !open && setResetTarget(null)}
        onConfirm={handleConfirmReset}
        title={t("adminTrials.reset.confirmTitle")}
        description={t("adminTrials.reset.confirmDescription", { device: resetTarget?.device_key || "" })}
        confirmText={t("adminTrials.reset.confirmButton")}
        cancelText={t("adminTrials.reset.cancel")}
        variant="destructive"
      />
    </div>
  );
}
